import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import AdminSearch from '../../components/admin/AdminSearch';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const fetchCustomers = useCallback(() => {
    setLoading(true);
    api.get('/users/customers', { params: search ? { search } : {} })
      .then((res) => setCustomers(res.data))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(fetchCustomers, [fetchCustomers]);

  const toggleBlock = async (id) => {
    try {
      await api.patch(`/users/customers/${id}/block`);
      toast.success('Customer status updated');
      fetchCustomers();
    } catch (e) {
      toast.error('Could not update customer');
    }
  };

  const exportCSV = () => {
    if (!customers.length) {
      toast.error('No customers available to export');
      return;
    }
    const headers = ['Customer Name', 'Email', 'Phone', 'Joined Date', 'Status'];
    const rows = customers.map((c) => [
      `"${c.name}"`,
      `"${c.email}"`,
      c.phone || 'N/A',
      new Date(c.createdAt).toLocaleDateString(),
      c.isActive ? 'Active' : 'Blocked',
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Chandana_Aura_Customers_Report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Customers CSV Report downloaded');
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Registered Customers ({customers.length})</h1>
          <p className="text-xs text-[#8A796C]">Manage customer accounts, active access, and contact details.</p>
        </div>

        <div className="flex w-full lg:w-auto flex-col sm:flex-row gap-3">
          <AdminSearch value={searchInput} onChange={setSearchInput} onSearch={setSearch} placeholder="Search name, email or phone" />
          <button
            onClick={exportCSV}
            className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded shadow transition-colors"
          >
            ↓ Export Customers CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E8DFD8] rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#F5EFEB] text-[#2B1810] font-heading uppercase text-[11px] font-bold tracking-wider border-b border-[#E8DFD8]">
              <tr>
                <th className="p-4">Customer Name</th>
                <th className="p-4">Email Address</th>
                <th className="p-4">Phone Number</th>
                <th className="p-4">Registered Date</th>
                <th className="p-4">Account Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8DFD8]">
              {loading ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={6}>Loading customer list...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td className="p-6 text-center text-[#8A796C]" colSpan={6}>No customers registered yet.</td></tr>
              ) : (
                customers.map((c) => (
                  <tr key={c._id} className="hover:bg-[#FAF8F5] transition-colors">
                    <td className="p-4 font-bold text-[#2B1810]">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-[#2B1810] text-[#C49A45] font-bold text-xs flex items-center justify-center border border-[#C49A45]">
                          {c.name?.[0] || 'C'}
                        </div>
                        <span>{c.name}</span>
                      </div>
                    </td>
                    <td className="p-4 font-medium text-[#2B1810]">{c.email}</td>
                    <td className="p-4 text-[#8A796C]">{c.phone || '—'}</td>
                    <td className="p-4 text-[#8A796C]">{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                        c.isActive ? 'bg-[#E8F3EB] text-[#2E5E3E]' : 'bg-red-100 text-red-800'
                      }`}>
                        {c.isActive ? 'Active' : 'Blocked'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => toggleBlock(c._id)}
                        className={`font-bold text-xs py-1 px-3 rounded border transition-colors ${
                          c.isActive ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                        }`}
                      >
                        {c.isActive ? 'Block Account' : 'Unblock Account'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
